import QRCode from "qrcode";
// qrcode's own SVG renderer, called directly because it is synchronous
// (QRCode.toString is async); the dialog preview sets an <img src> in place
import { render as renderSvgTag } from "qrcode/lib/renderer/svg-tag.js";

const QR_OPTIONS = { errorCorrectionLevel: "M" };
const MARGIN = 4;
const PDF_SIZE_MM = 100;
// a size for design tools (Illustrator, Inkscape), which open a bare viewBox tiny
const SVG_DOWNLOAD_PX = 1024;

export const QR_FORMATS = [
  {
    id: "png-large",
    label: "PNG, large",
    hint: "Word, PowerPoint, home printers, posters up to A3",
  },
  {
    id: "svg",
    label: "SVG",
    hint: "Print shops and designers (Illustrator, Figma, Inkscape). Sharp at any size",
  },
  {
    id: "pdf",
    label: "PDF",
    hint: "Print it yourself or email it to a print shop",
  },
  {
    id: "png-small",
    label: "PNG, small",
    hint: "Websites, emails and social posts",
  },
];

const PNG_WIDTH = { "png-large": 2048, "png-small": 512 };

export function qrCodeText(fullUrl) {
  return `${fullUrl}?via_qrcode=1`;
}

function qrCodeSvg(fullUrl, width) {
  return renderSvgTag(QRCode.create(qrCodeText(fullUrl), QR_OPTIONS), {
    margin: MARGIN,
    width,
  });
}

export function qrCodeSvgDataUri(fullUrl) {
  return `data:image/svg+xml;base64,${btoa(qrCodeSvg(fullUrl))}`;
}

async function pngBlob(fullUrl, width) {
  const dataUrl = await QRCode.toDataURL(qrCodeText(fullUrl), {
    width,
    margin: MARGIN,
    ...QR_OPTIONS,
  });
  const bytes = Uint8Array.from(atob(dataUrl.split(",")[1]), (c) =>
    c.charCodeAt(0),
  );
  return new Blob([bytes], { type: "image/png" });
}

async function pdfBlob(fullUrl) {
  // loaded on demand: jsPDF is large and only this format needs it
  const { jsPDF } = await import("jspdf");
  const { modules } = QRCode.create(qrCodeText(fullUrl), QR_OPTIONS);
  const cells = modules.size + MARGIN * 2;
  const cell = PDF_SIZE_MM / cells;
  const doc = new jsPDF({ unit: "mm", format: [PDF_SIZE_MM, PDF_SIZE_MM] });
  doc.setFillColor(0, 0, 0);
  // one rectangle per horizontal run: touching squares show hairline seams
  // in some PDF viewers
  for (let row = 0; row < modules.size; row++) {
    let col = 0;
    while (col < modules.size) {
      if (!modules.get(row, col)) {
        col++;
        continue;
      }
      const start = col;
      while (col < modules.size && modules.get(row, col)) col++;
      doc.rect(
        (start + MARGIN) * cell,
        (row + MARGIN) * cell,
        (col - start) * cell,
        cell,
        "F",
      );
    }
  }
  return doc.output("blob");
}

export async function qrCodeFile(fullUrl, formatId) {
  if (!fullUrl) {
    throw new Error("QR code not available");
  }
  const name = `1link_qrcode_${fullUrl.split("/").pop()}`;
  if (formatId === "svg") {
    return {
      blob: new Blob([qrCodeSvg(fullUrl, SVG_DOWNLOAD_PX)], {
        type: "image/svg+xml",
      }),
      filename: `${name}.svg`,
    };
  }
  if (formatId === "pdf") {
    return { blob: await pdfBlob(fullUrl), filename: `${name}.pdf` };
  }
  if (!PNG_WIDTH[formatId]) {
    throw new Error(`Unknown QR format: ${formatId}`);
  }
  return {
    blob: await pngBlob(fullUrl, PNG_WIDTH[formatId]),
    filename: `${name}.png`,
  };
}
